import * as THREE from "three";
import * as TWEEN from "@tweenjs/tween.js";
import {AVATAR_ANIMATION} from "utils/constants";

const _box = new THREE.Box3();

// For OOBC Variables
const global = {};

class OOBCApi {
  constructor(context) {
    this.context = context;
    this.script = null;

    this.actionObject = undefined; // OOBC Action
    this.targetObject = undefined; // OOBC Target
    this.signalHandlers = {};
    this.timer = {
      state: "off",
      timeStarted: 0,
      timePaused: 0,
      currentTime: 0,
    };
    this.delay = ms => new Promise(res => setTimeout(res, ms)); // OOBC Wait Function
  }

  runUserScript() {
    this.script = `const init = async() => {\n
      ${this.script}
    } \n init()`;
    eval(this.script);
  }

  setCameraProps() {
    this.context.scenePlayCameras.forEach(camera => {
      const {enabled, depth} = camera.userData;
      const context = this.context;
      Object.defineProperties(camera, {
        enabled: {value: enabled, writable: true},
        _depth: {value: depth, writable: true},
        depth: {
          get() {
            return this._depth;
          },
          set(value) {
            this._depth = value;
            context.updateCameraOrder();
          },
        },
      });
    });
  }

  getSprite(uuid) {
    if (uuid === "0") {
      this.actionObject = this.context.avatar.model;
    } else {
      this.actionObject = this.context.scenePlay.getObjectByProperty("uuid", uuid);
      if (!this.actionObject) {
        this.actionObject = this.context.scenePlayGUI?.getObjectByProperty("uuid", uuid);
        if (!this.actionObject) this.actionObject = this.context.sceneGUI?.getObjectByProperty("uuid", uuid);
      }
    }
    return this;
  }

  setText(text) {
    if (this.actionObject.type !== "Sprite") return;
    this.actionObject.getObjectByProperty("isCSS2DObject", true).element.innerText = text;
  }

  onCollide(uuid, callback) {
    let instanceObj = this.actionObject;
    let target = uuid === "0" ? this.context.avatar.model : this.context.scenePlay.getObjectByProperty("uuid", uuid);

    target.body.on.collision((object, event) => {
      if (object === instanceObj && event === "start") {
        callback.bind(this)();
      }
    });
  }

  onCollideOnce(uuid, callback) {
    const instanceObj = this.actionObject;
    const target = uuid === "0" ? this.context.avatar.model : this.context.scenePlay.getObjectByProperty("uuid", uuid);

    target.body.on.collision((object, event) => {
      if (object === instanceObj && event === "start") {
        if (callback) callback.bind(this)();
        callback = false;
      }
    });
  }

  onCollideEnd(uuid, callback) {
    const instanceObj = this.actionObject;
    const target = uuid === "0" ? this.context.avatar.model : this.context.scenePlay.getObjectByProperty("uuid", uuid);

    target.body.on.collision((object, event) => {
      if (object === instanceObj && event === "end") {
        callback.bind(this)();
      }
    });
  }

  onContact(uuid, callback) {
    const instanceObj = this.actionObject;
    const target = uuid === "0" ? this.context.avatar.model : this.context.scenePlay.getObjectByProperty("uuid", uuid);

    target.body.on.collision((object, event) => {
      if (object === instanceObj && event === "collision") {
        callback.bind(this)();
      }
    });
  }

  goTo(dx, dy, dz) {
    if (!this.actionObject) return;
    const x = dx ? dx : this.actionObject.position.x;
    const y = dy ? dy : this.actionObject.position.y;
    const z = dz ? dz : this.actionObject.position.z;

    this.actionObject.position.set(x, y, z);
    if (this.actionObject.body) this.actionObject.body.needUpdate = true;
  }

  makeGoSpriteFunc = (...position) => {
    if (this.actionObject.body) return;
    const ox = this.actionObject.userData.guiTransform.offset.x.value;
    const oy = this.actionObject.userData.guiTransform.offset.y.value;

    this.actionObject.userData.guiTransform.offset.x.value = position[0].x ? ox + position[0].x : ox;
    this.actionObject.userData.guiTransform.offset.y.value = position[0].y ? oy + position[0].y : oy;
  };
  makeGoFunc = (...position) => {
    if (!this.actionObject) return;
    const {x: ox, y: oy, z: oz} = this.actionObject.position;

    const x = position[0].x ? ox + position[0].x : ox;
    const y = position[0].y ? oy + position[0].y : oy;
    const z = position[0].z ? oz + position[0].z : oz;

    this.actionObject.position.set(x, y, z);
    if (this.actionObject.body) this.actionObject.body.needUpdate = true;
  };

  goX(x) {
    this.makeGoFunc({x});
  }
  goY(y) {
    this.makeGoFunc({y});
  }
  goZ(z) {
    this.makeGoFunc({z});
  }

  makeRotateFunc = (axis, value) => {
    if (!this.actionObject) return;
    this.actionObject.rotation[axis] = value;
    if (this.actionObject.body) this.actionObject.body.needUpdate = true;
  };
  rotateX(value) {
    this.makeRotateFunc("x", value);
  }
  rotateY(value) {
    this.makeRotateFunc("y", value);
  }
  rotateZ(value) {
    this.makeRotateFunc("z", value);
  }

  moveTo(x, y, z) {
    if (!this.actionObject) return;
    let duration = Math.sqrt(x ** 2 + y ** 2 + z ** 2) * 1000;
    if (this.actionObject.type === "Sprite") {
      duration = Math.sqrt(x ** 2 + y ** 2 + z ** 2) * 10;
      const targetPosition = {
        x: this.actionObject.position.x + x,
        y: this.actionObject.position.y + y,
        z: this.actionObject.position.z + z,
      };
      new TWEEN.Tween(this.actionObject.position).to(targetPosition, duration).start();
    }
    if (!this.actionObject.body) return;
    if (this.actionObject.body.getCollisionFlags() !== 6) this.actionObject.body.setCollisionFlags(2);
    // sharing actionObject problem - continuously updated object breaks when actionobject change.
    const object = this.actionObject;
    new TWEEN.Tween(object.position)
      .to(
        {
          x: x >= 0 ? `+${x}` : `${x}`,
          y: y >= 0 ? `+${y}` : `${y}`,
          z: z >= 0 ? `+${z}` : `${z}`,
        },
        duration,
      )
      .onUpdate(() => {
        object.body.needUpdate = true;
      })
      .start();
  }

  makeMoveFunc(axis, value) {
    if (!this.actionObject) return;
    const {position, body, type} = this.actionObject;
    let duration = Math.abs(value) * 1000;

    if (!body && type === "Sprite") {
      const targetPosition = position[axis] + value;
      duration = Math.abs(value) * 10;
      new TWEEN.Tween(position).to({[axis]: targetPosition}, duration).start();
    } else {
      if (body.getCollisionFlags() !== 6) body.setCollisionFlags(2);
      new TWEEN.Tween(position)
        .to({[axis]: value >= 0 ? `+${value}` : `${value}`}, duration)
        .onUpdate(() => {
          body.needUpdate = true;
        })
        .start();
    }
  }

  moveX(dx) {
    this.makeMoveFunc("x", dx);
  }
  moveY(dy) {
    this.makeMoveFunc("y", dy);
  }
  moveZ(dz) {
    this.makeMoveFunc("z", dz);
  }

  turn(axis, value) {
    if (!this.actionObject) return;
    const speed = 90;
    const duration = (Math.abs(value) / speed) * 1000;
    value *= Math.PI / 180;

    if (!this.actionObject.body) return;
    if (this.actionObject.body.getCollisionFlags() !== 6) this.actionObject.body.setCollisionFlags(2);

    const newRotation = {
      x: this.actionObject.rotation._x + (axis === "x" ? value : 0),
      y: this.actionObject.rotation._y + (axis === "y" ? value : 0),
      z: this.actionObject.rotation._z + (axis === "z" ? value : 0),
    };

    const object = this.actionObject;

    new TWEEN.Tween(object.rotation)
      .to(newRotation, duration)
      .onUpdate(() => {
        object.body.needUpdate = true;
      })
      .start();
  }

  kill() {
    if (!this.actionObject) return;
    if (this.actionObject.type === "Sprite") {
      this.actionObject.traverse(object => {
        object.layers.disableAll();
        object.visible = false;
      });
      return;
    }
    this.actionObject.visible = false;

    this.actionObject.traverse(child => {
      child.layers.disableAll();
      if (child.type === "Audio") child.stop();
    });

    if (this.actionObject.body) {
      this.actionObject.body.checkCollisions = false;
      this.actionObject.body.setCollisionFlags(5);
    }
  }

  revive() {
    if (!this.actionObject) return;
    if (this.actionObject.type === "Sprite") {
      this.actionObject.traverse(object => {
        object.layers.enableAll();
        object.visible = true;
      });
      return;
    }

    this.actionObject.visible = true;
    this.actionObject.traverse(child => {
      child.layers.enableAll();
    });
    if (this.actionObject.body) {
      this.actionObject.body.checkCollisions = true;
      this.actionObject.body.setCollisionFlags(2);
    }
  }

  async wait(s, callback) {
    await this.delay(s * 1000);
    if (callback) callback.bind(this)();
  }

  spawn() {
    if (this.context.avatar.model !== undefined && this.context.startLocation !== undefined) {
      _box.setFromObject(this.context.avatar.model);
      const offset = 1 + (_box.max.y - _box.min.y) / 2;
      _box.setFromObject(this.context.startLocation);
      this.context.avatar.model.body.transform();
      this.context.avatar.model.body.setPosition(
        this.context.startLocation.position.x,
        _box.max.y + offset,
        this.context.startLocation.position.z,
      );
      this.context.avatar.model.body.refresh();
    }
  }

  playAnimation(name, repeat) {
    if (name === "die") {
      this.context.avatar.playAnimation(AVATAR_ANIMATION.DIE);
    }
    if (name === "victory") {
      this.context.avatar.playAnimation(AVATAR_ANIMATION.VICTORY);
    }
  }

  playVideo() {
    let instanceObj = this.actionObject;
    let videoElement = this.context.videoElements[instanceObj.uuid];
    videoElement.play();
  }

  stopVideo() {
    let instanceObj = this.actionObject;
    let videoElement = this.context.videoElements[instanceObj.uuid];
    videoElement.stop();
  }

  sendSignal(signal) {
    if (this.signalHandlers[signal]) {
      this.signalHandlers[signal]();
    }
  }

  onSignal(signal, callback) {
    this.signalHandlers[signal] = callback.bind(this);
  }

  changePlayerSpeed(speed) {
    this.context.avatar.model.manager.speedFactor = speed;
  }

  // onClick(callback) {
  // if (!this.actionObject.body) return;
  // if (this.actionObject.onClickObject === undefined) this.actionObject.onClickObject = [];
  // this.actionObject.onClickObject.push(callback.bind(this.actionObject));
  // }

  startTimer() {
    if (this.timer.state !== "off") return;
    this.timer.state = "on";
    this.timer.timeStarted = performance.now();
  }

  pauseTimer() {
    if (this.timer.state !== "on") return;
    this.timer.state = "paused";
    this.timer.timePaused = performance.now();
    this.timer.currentTime = (this.timer.timePaused - this.timer.timeStarted) / 1000;
  }

  resumeTimer() {
    if (this.timer.state !== "paused") return;
    this.timer.state = "on";
    this.timer.timeStarted += performance.now() - this.timer.timePaused;
  }

  resetTimer() {
    this.timer.state = "off";
    this.timer.currentTime = 0;
  }

  getTimer() {
    if (this.timer.state === "on") {
      this.timer.currentTime = (performance.now() - this.timer.timeStarted) / 1000;
    }
    return this.timer.currentTime.toFixed(2);
  }
}

export default OOBCApi;
